import axios from 'axios';
import { clearStoredAuth, readStoredAuth, writeStoredAuth } from '@/auth/authStorage';

function resolveBaseUrl() {
  const envUrl = import.meta.env.VITE_API_URL?.trim();

  if (envUrl) {
    // Prevent production bundles from accidentally calling a browser-local backend.
    const isLocalEnvTarget = /^https?:\/\/(localhost|127\.0\.0\.1)(:\d+)?(\/|$)/i.test(envUrl);
    const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
    const isBrowserLocal = hostname === 'localhost' || hostname === '127.0.0.1';

    if (!(isLocalEnvTarget && !isBrowserLocal)) {
      return envUrl;
    }
  }

  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    const isBrowserLocal = hostname === 'localhost' || hostname === '127.0.0.1';
    return isBrowserLocal ? 'http://localhost:8000' : window.location.origin;
  }

  return 'http://localhost:8000';
}

const rawBaseUrl = resolveBaseUrl();
const normalizedBaseUrl = rawBaseUrl.replace(/\/$/, '').replace(/\/api$/, '');

const http = axios.create({
  baseURL: normalizedBaseUrl,
  timeout: 15000,
});

http.interceptors.request.use((config) => {
  if (typeof window !== 'undefined') {
    const stored = readStoredAuth();
    if (stored?.accessToken) {
      config.headers.Authorization = `Bearer ${stored.accessToken}`;
    }
  }

  return config;
});

let refreshPromise: Promise<string | null> | null = null;

async function refreshAccessToken(): Promise<string | null> {
  if (refreshPromise) {
    return refreshPromise;
  }

  const stored = readStoredAuth();
  if (!stored?.refreshToken) {
    clearStoredAuth();
    return null;
  }

  refreshPromise = axios
    .post(`${normalizedBaseUrl}/api/auth/refresh`, {
      refresh_token: stored.refreshToken,
    })
    .then((response) => {
      const data = response.data as {
        access_token: string;
        refresh_token: string;
        user: typeof stored.user;
      };

      writeStoredAuth({
        accessToken: data.access_token,
        refreshToken: data.refresh_token,
        user: data.user,
      });

      return data.access_token;
    })
    .catch(() => {
      clearStoredAuth();
      return null;
    })
    .finally(() => {
      refreshPromise = null;
    });

  return refreshPromise;
}

http.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config as (typeof error.config & { _retry?: boolean }) | undefined;
    if (!originalRequest) {
      return Promise.reject(error);
    }

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      const newAccessToken = await refreshAccessToken();

      if (newAccessToken) {
        originalRequest.headers = originalRequest.headers ?? {};
        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
        return http(originalRequest);
      }
    }

    return Promise.reject(error);
  },
);

export default http;
